'use client';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import { useEffect, useState, useRef, ReactNode } from 'react';
import CustomRichText from '../CustomRichText';

export interface StudyFormatBlockProps {
  title: ReactNode | SerializedEditorState;
  subtitle: ReactNode | SerializedEditorState;
  txtContent: ReactNode | SerializedEditorState;
  list: Array<string> | Array<{ item: ReactNode | SerializedEditorState }>;
  index: number;
  color: 'violet' | 'green' | 'blue';
}

export default function StudyFormatCardV2({
  title,
  subtitle,
  txtContent,
  list,
  index,
  color,
}: StudyFormatBlockProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(true);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Check if the device is a touch device
    const checkTouchDevice = () => {
      if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
        setIsTouchDevice(true);
      }
    };

    checkTouchDevice();

    // Add a resize event listener to check screen size and touch device on screen resize
    const handleResize = () => {
      checkTouchDevice();
      setIsSmallScreen(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);

    // Initial check for screen size
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isSmallScreen || !isTouchDevice) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.9 }, // Trigger animation when 90% of the card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isSmallScreen, isTouchDevice]);

  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setIsHovered(false);
    }
  };

  const handleClick = () => {
    if (isTouchDevice) {
      setIsHovered(!isHovered);
    }
  };

  const backgroundColorClass =
    {
      green: {
        border: 'file-border-green',
        bg: 'file-green',
        line: 'border-green-300',
        width: 'max-w-[400px]',
        height: 'max-h-[550px]',
      },
      blue: {
        border: 'file-border-blue',
        bg: 'file-blue',
        line: 'border-blue-300',
        width: 'max-w-[400px]',
        height: 'max-h-[325px]',
      },
      violet: {
        border: 'file-border-violet',
        bg: 'file-violet',
        line: 'border-violet-300',
        width: 'sm:w-[400px] w-[350px]',
        height: 'sm:h-[350px] h-[300px]',
      },
    }[color] || 'custom-gradient-green';

  return (
    <div
      data-meta="StudyFormatCard"
      ref={cardRef}
      className={`overflow-hidden text-white`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <div
        className={`
            relative
            min-h-fit
            ${backgroundColorClass.width}
            ${backgroundColorClass.height}
            transition-transform
            duration-[1000ms]
            ease-in-out
            transform-style-preserve-3d
            ${isHovered || (isTouchDevice && isSmallScreen && isInView) ? 'rotate-x-180' : ''}
            `}
      >
        <div
          className={`absolute inset-0 px-8 pt-8 ${
            color === 'violet' ? 'pb-16' : 'pb-4'
          } transition-opacity duration-[700ms] ease-in-out ${
            isHovered || (isTouchDevice && isSmallScreen && isInView) ? 'opacity-0' : 'opacity-100'
          } backface-hidden ${backgroundColorClass.border} `}
        >
          <div className={`pb-4 border-b ${backgroundColorClass.line} mb-2 max-w-[350px]`}>
            <CustomRichText data={title} tag="div" className="text-xl font-bold" />
            <CustomRichText data={subtitle} tag="div" className="text-xs" />
          </div>
          <ul className="list-disc pl-4">
            {list.map((item, index) => (
              <li key={index} className="">
                {typeof item === 'string' ? (
                  item
                ) : (
                  <CustomRichText data={item.item} tag="span" className="" />
                )}
              </li>
            ))}
          </ul>
        </div>
        <div
          className={`absolute inset-0 flex items-center justify-center pr-12 pl-8 sm:py-8 ${backgroundColorClass.bg} backface-hidden rotate-x-180`}
        >
          <CustomRichText data={txtContent} tag="div" className="text-center text-sm" />
        </div>
      </div>
    </div>
  );
}
